import { z } from "zod";

import { coerceColor, entityColorSchema } from "./color";

/**
 * R17 A08A-MODEL-SCHEMA-BARREL — behaviour-preserving extraction.
 *
 * Shared 2D/3D coordinate primitives and the common entity base schema,
 * moved verbatim out of the red `model.ts` (no other repository owner).
 * `model.ts` re-exports every symbol below at its original path so no
 * external import changes; this module is the first landing point for the
 * remaining `A08B`…`A08L` per-entity-family schema extractions, each of
 * which imports these primitives directly instead of re-deriving them.
 *
 * `drawingEntitySchema` / `blockChildEntitySchema` (the top-level "type"
 * discriminated unions) are NOT moved here: every one of their members is
 * still a same-file sibling schema in `model.ts` (Text/MText, Dimension/
 * MLeader, Hatch/RevCloud, Block/Attdef, Array, Viewport, Wipeout, Curve,
 * Poly/Spline — see `A08B`…`A08L`). Relocating the union declarations
 * before their members move would force a same-package circular import
 * (this module importing the per-type schemas back from `model.ts`, which
 * in turn imports the unions from this module) that breaks under ESM's
 * temporal-dead-zone evaluation order — the exact class of latent runtime
 * defect this task's instructions call out. The unions move naturally,
 * with zero circularity, once their members have relocated through the
 * later cards in this family.
 */

export const drawingPointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const drawingPoint3DSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().default(0),
});

/**
 * 3D point with an *optional* z (DEFUSE S1b). Used by entity geometry
 * fields that are pervasively constructed as bare 2D `{x, y}` literals by
 * the web reducers / compact entity store / AI command builders (e.g.
 * LINE start/end, POINT location). Unlike `drawingPoint3DSchema` — whose
 * `.default(0)` makes `z` *required* on the inferred output type and would
 * therefore reject the thousands of existing `{x, y}` literals — this
 * schema keeps `z` optional (`z?: number`) so those literals typecheck
 * unchanged, while still carrying a real z when one is present. The DXF
 * bridge supplies z on import (`toDrawingPoint3D`, group 30/31) and emits
 * `z ?? 0` on export (`toDxfPoint3D`), so the 3D coordinate round-trips
 * even though the field is optional in the model.
 */
export const drawingPointOptionalZSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().optional(),
});

export const drawingEntityBaseSchema = z.object({
  id: z.string().min(1),
  spaceId: z.string().min(1),
  layer: z.string().default("0"),
  // EPIC-COLOR-UNION S1 — the per-entity colour is the typed `EntityColor`
  // union (DXF group 62 / 420 / 430), not a bare string. `z.preprocess`
  // coerces any legacy string (`"ByLayer"`/`"5"`/`"#rrggbb"`) AND an already-
  // typed object idempotently, so every existing `color: "..."` literal in
  // reducers / tests / AI builders keeps typechecking (the schema's INPUT type
  // stays permissive) while the OUTPUT type is `EntityColor`. The legacy string
  // is recovered for the renderer / property bag via `colorToLegacyString`.
  color: z
    .preprocess(coerceColor, entityColorSchema)
    .default({ kind: "byLayer" }),
  linetype: z.string().default("ByLayer"),
  lineweight: z.string().default("ByLayer"),
  transparency: z.string().default("0"),
  /**
   * Per-entity linetype scale — DXF group 48 (CELTSCALE-per-entity). The
   * displayed / plotted dash pattern length for this entity is multiplied by
   * this factor (on top of the drawing-level $LTSCALE). AutoCAD default is 1.
   * Threaded into the PDF dash emitter (`DashEmitOptions.sysvars.CELTSCALE`)
   * so a DASHED entity with a non-1 scale plots at the authored dash length.
   *
   * Optional (read as `?? 1`) like the other additive base fields
   * (arrayGroupId / arrayMode) so the thousands of entity literals built by
   * reducers / tests / AI command builders need not be touched; the bridge
   * sets it on import only when the DXF carried a non-default group 48.
   */
  linetypeScale: z.number().optional(),
  /**
   * Array membership tracking — set on all entities that participate in
   * an ARRAYRECT / ARRAYPOLAR / ARRAYPATH creation (both the original
   * source entities and their generated copies share the same groupId).
   * Used by ARRAYEDIT to locate and re-generate array copies when the
   * user edits array parameters.
   */
  arrayGroupId: z.string().optional(),
  /**
   * Marks an entity as a source (original, pre-array) member of the
   * given arrayGroupId. Only source entities carry this field; the
   * generated copies carry only `arrayGroupId` without this marker. On
   * ARRAYEDIT, the reducer deletes every entity whose `arrayGroupId`
   * matches but which lacks `arraySourceOfGroup`, then re-runs the
   * array generation from the sources with the new parameters.
   */
  arraySourceOfGroup: z.string().optional(),
  /**
   * Array mode tag — records which create command produced this entity
   * (rect / polar / path). Set on both source entities and copies at
   * creation time and preserved across DXF round-trip via XDATA. Used
   * by the ARRAYEDIT UI to pick the correct edit flow when the user
   * clicks a member of an existing array group.
   */
  arrayMode: z.enum(["rect", "polar", "path"]).optional(),
  /**
   * DXF common reference handles + flags (S-COMMON-HANDLES). Parsed and
   * written by dxf-io (entityParser / writer) but previously dropped at
   * the model boundary — these 5 additive-optional fields carry them
   * across the import→model→export round-trip so AutoCAD-authored values
   * survive a save:
   *   ownerHandle      — DXF group 330 (owner BLOCK_RECORD / object handle)
   *   materialHandle   — DXF group 347 (AcDbMaterial reference)
   *   plotStyleHandle  — DXF group 390 (plot-style-name object handle)
   *   visibility       — DXF group 60  (0 = visible, 1 = invisible)
   *   shadowMode       — DXF group 284 (0 casts+receives … 3 ignores)
   * Optional like the other additive base fields so the thousands of
   * entity literals built by reducers / tests / AI builders need not be
   * touched; the bridge sets them on import only when the DXF carried a
   * value, and emits them on export only when present (shadowMode skipped
   * when 0 — the AutoCAD default — to keep writer output compact).
   */
  ownerHandle: z.string().optional(),
  materialHandle: z.string().optional(),
  plotStyleHandle: z.string().optional(),
  visibility: z.number().int().optional(),
  shadowMode: z.number().int().optional(),
  /**
   * DXF entity extension-dictionary handle — the 360 inside the entity's
   * `102 {ACAD_XDICTIONARY 360 <handle> 102 }` block. Parsed onto
   * `common.extensionDictionaryHandle` by the entity parser. Carried on the
   * model so the associative-array second pass (Batch-8 S-ARRAYASSOC-RESOLVE)
   * can join an ARRAY entity to its AcDbAssocArray OBJECTS graph, AND so a
   * full DrawingDocument round-trip re-emits the entity's ext-dict 360
   * linkage. Additive-optional like the handles above. (Previously only
   * INSERT representation-data set this via geometry.representationData; this
   * lifts it to the common base for arrays + any other ext-dict carrier.)
   */
  extensionDictionaryHandle: z.string().optional(),
  /**
   * Entity-level unknown / vendor DXF group passthrough (S-COMMON-XDATA).
   * Raw round-trip of any DXF groups the dxf-io entity parser captured but
   * did not map to a typed field (vendor-specific extensions, future codes,
   * application XData appended to the common record). Parsed into
   * `draft.extraGroups` (entityParser) and re-emitted verbatim by the writer
   * (`groups.push(...entity.extraGroups)`) — these were previously dropped at
   * the model boundary, so the passthrough was lost on a round-trip.
   *
   * The shape is intentionally a drawing-model-LOCAL `{ code, value }` array
   * structurally identical to dxf-io's `DxfGroup`; model.ts is the canonical
   * core and must NOT import from packages/dxf-io. The bridge files map the
   * `DxfGroup[]` ↔ this shape with a plain array copy (no conversion needed).
   *
   * Distinct from the per-ATTRIB `rawGroups` (a different mechanism, on the
   * blockInsertAttributeSchema) and the document-level `rawEntityGroups` —
   * this is the entity-level group-array passthrough. Optional like the other
   * additive base fields so existing entity literals need not be touched; the
   * bridge sets it on import only when the DXF carried unknown groups.
   */
  extraGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),
});

export type DrawingPoint = z.infer<typeof drawingPointSchema>;
export type DrawingPoint3D = z.infer<typeof drawingPoint3DSchema>;
export type DrawingEntityBase = z.infer<typeof drawingEntityBaseSchema>;
